export const SavaFRole = {
  PLATFORM_ADMIN: 'platform_admin',
  CURATOR_ADMIN: 'curator_admin',
  CURATOR: 'curator',
} as const;

export type SavaFRole = (typeof SavaFRole)[keyof typeof SavaFRole];

export const Permission = {
  SUBMISSION_VIEW: 'submission:view',
  TRIAGE_VIEW: 'triage:view',
  SCORE_WRITE: 'score:write',
  NOTES_INTERNAL: 'notes:internal',
  NOTES_EXTERNAL: 'notes:external',
  PROMOTION_CREATE: 'promotion:create',
  SUBMISSION_VIEW_GLOBAL: 'submission:view:global',

  THESIS_MANAGE: 'thesis:manage',
  ORG_BRANDING: 'org:branding',
  ORG_EMAIL: 'org:email',
  ORG_REPORTING: 'org:reporting',
  ORG_DESTINATIONS: 'org:destinations',
  ORG_AUDIT_READ: 'org:audit:read',
  ORG_MEMBERS_INVITE: 'org:members:invite',
  ORG_MEMBERS_REMOVE: 'org:members:remove',

  PLATFORM_ORG_CREATE: 'platform:org:create',
  PLATFORM_ORG_LIFECYCLE: 'platform:org:lifecycle',
  PLATFORM_ORG_LINK: 'platform:org:link',
  PLATFORM_ROUTING: 'platform:routing',
  PLATFORM_DNS: 'platform:dns',
  PLATFORM_AI: 'platform:ai',
  PLATFORM_FLAGS: 'platform:flags',
  PLATFORM_MONITORING: 'platform:monitoring',
  PLATFORM_ABUSE: 'platform:abuse',
  PLATFORM_AUDIT_READ: 'platform:audit:read',
} as const;

export type Permission = (typeof Permission)[keyof typeof Permission];

const CURATOR_PERMISSIONS: ReadonlySet<Permission> = new Set([
  Permission.SUBMISSION_VIEW,
  Permission.TRIAGE_VIEW,
  Permission.SCORE_WRITE,
  Permission.NOTES_INTERNAL,
  Permission.NOTES_EXTERNAL,
  Permission.PROMOTION_CREATE,
  Permission.ORG_DESTINATIONS,
]);

const CURATOR_ADMIN_PERMISSIONS: ReadonlySet<Permission> = new Set([
  ...CURATOR_PERMISSIONS,
  Permission.THESIS_MANAGE,
  Permission.ORG_BRANDING,
  Permission.ORG_EMAIL,
  Permission.ORG_REPORTING,
  Permission.ORG_DESTINATIONS,
  Permission.ORG_AUDIT_READ,
  Permission.ORG_MEMBERS_INVITE,
  Permission.ORG_MEMBERS_REMOVE,
]);

const PLATFORM_ADMIN_PERMISSIONS: ReadonlySet<Permission> = new Set([
  ...CURATOR_ADMIN_PERMISSIONS,
  Permission.SUBMISSION_VIEW_GLOBAL,
  Permission.PLATFORM_ORG_CREATE,
  Permission.PLATFORM_ORG_LIFECYCLE,
  Permission.PLATFORM_ORG_LINK,
  Permission.PLATFORM_ROUTING,
  Permission.PLATFORM_DNS,
  Permission.PLATFORM_AI,
  Permission.PLATFORM_FLAGS,
  Permission.PLATFORM_MONITORING,
  Permission.PLATFORM_ABUSE,
  Permission.PLATFORM_AUDIT_READ,
]);

const ROLE_PERMISSIONS: Record<SavaFRole, ReadonlySet<Permission>> = {
  [SavaFRole.CURATOR]: CURATOR_PERMISSIONS,
  [SavaFRole.CURATOR_ADMIN]: CURATOR_ADMIN_PERMISSIONS,
  [SavaFRole.PLATFORM_ADMIN]: PLATFORM_ADMIN_PERMISSIONS,
};

export function getPermissionsForRole(role: SavaFRole): ReadonlySet<Permission> {
  return ROLE_PERMISSIONS[role];
}

export function hasPermission(role: SavaFRole, permission: Permission): boolean {
  return ROLE_PERMISSIONS[role].has(permission);
}

export function resolveRole(claims: {
  systemRole?: string;
  system_role?: string;
  workspaceRole?: string;
  workspace_role?: string;
}): SavaFRole {
  const sysRole = claims.systemRole ?? claims.system_role;
  if (sysRole === 'SUPER_ADMIN') {
    return SavaFRole.PLATFORM_ADMIN;
  }

  const wsRole = claims.workspaceRole ?? claims.workspace_role;
  switch (wsRole) {
    case 'OWNER':
    case 'ADMIN':
      return SavaFRole.CURATOR_ADMIN;
    case 'MEMBER':
      return SavaFRole.CURATOR;
    default:
      return SavaFRole.CURATOR;
  }
}
